import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Sparkles } from 'lucide-react';

import { ChatMode } from './AgentModeDropdown';
import { ModelType, getModelConfig } from './ModelDropdown';
import { cn } from '@/lib/utils';
import { PlanType } from '@optidev/shared-types';
import { usePlanFeatures } from '@/hooks/useFeatures';
import { useCodexEnabled } from '@/hooks/use-codex-enabled';
import { billingApi } from '@/lib/api/billing';
import { auth } from '@/lib/firebase';

interface AgentSettingsContentProps {
  mode: ChatMode;
  model: ModelType;
  onModeChange: (mode: ChatMode) => void;
  onModelChange: (model: ModelType) => void;
  isStreaming?: boolean;
  disabled?: boolean;
}

export function AgentSettingsContent({
  mode,
  model,
  onModeChange,
  onModelChange,
  isStreaming = false,
  disabled = false,
}: AgentSettingsContentProps) {
  const { planFeatures } = usePlanFeatures();
  const [planType, setPlanType] = useState<PlanType>(PlanType.FREE);
  const { enabled: codexEnabled } = useCodexEnabled();

  const models = getModelConfig({ includeCodex: codexEnabled });

  // Fetch user's subscription plan
  useEffect(() => {
    const fetchPlan = async () => {
      try {
        const token = await auth.getIdToken();
        if (token) {
          const subscription = await billingApi.getSubscription(token);
          setPlanType(subscription.planType);
        }
      } catch (error) {
        console.error('Failed to fetch subscription:', error);
        setPlanType(PlanType.FREE);
      }
    };
    fetchPlan();
  }, []);

  // Check if a model is allowed for the current plan
  const isModelAllowed = (modelKey: string): boolean => {
    const currentPlanFeatures = planFeatures[planType] || planFeatures.free;
    const tier = models[modelKey]?.planTier;
    if (!tier) return true;
    return currentPlanFeatures.models.includes(tier);
  };

  const handleModelSelect = (modelOption: ModelType) => {
    if (!isModelAllowed(modelOption)) {
      return;
    }
    onModelChange(modelOption);
  };

  return (
    <div className="grid gap-4">
      {/* Agent Mode Section */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">
          Mode
        </label>
        <p className="text-xs text-muted-foreground">
          Choose how the agent interacts with your code
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          {(['plan', 'execute'] as const).map((modeOption) => (
            <button
              key={modeOption}
              onClick={() => onModeChange(modeOption)}
              disabled={isStreaming || disabled}
              className={cn(
                "flex-1 flex flex-col items-start gap-1 px-3 py-2.5 sm:px-4 sm:py-3 rounded-lg border text-sm",
                mode === modeOption
                  ? "border-primary bg-primary/10 text-foreground"
                  : "border-border hover:bg-accent text-muted-foreground",
                (isStreaming || disabled) && "opacity-50 cursor-not-allowed",
                "transition-all duration-200"
              )}
            >
              <div className="flex items-center justify-between w-full">
                <span className="font-medium capitalize">{modeOption}</span>
                {mode === modeOption && (
                  <div className="w-2 h-2 rounded-full bg-primary" />
                )}
              </div>
              <span className="text-xs text-muted-foreground">
                {modeOption === 'plan'
                  ? 'Analyze and plan changes'
                  : 'Write and modify code'}
              </span>
            </button>
          ))}
        </div>
      </div>

      {/* Model Selection Section */}
      <div className="space-y-2">
        <label className="text-sm font-medium text-foreground">
          Model
        </label>
        <p className="text-xs text-muted-foreground">
          Select AI model based on your needs
        </p>
        <div className="flex flex-col sm:flex-row gap-2">
          {Object.entries(models).map(
            ([modelKey, config]) => {
              const canUse = isModelAllowed(modelKey);

              return (
                <button
                  key={modelKey}
                  onClick={() => handleModelSelect(modelKey)}
                  disabled={isStreaming || disabled || !canUse}
                  className={cn(
                    "flex-1 flex flex-col items-start gap-1 px-3 py-2.5 sm:px-4 sm:py-3 rounded-lg border text-sm",
                    !canUse
                      ? "border-border opacity-60 cursor-not-allowed"
                      : model === modelKey
                      ? "border-primary bg-primary/10 text-foreground"
                      : "border-border hover:bg-accent text-muted-foreground",
                    (isStreaming || disabled) && canUse && "opacity-50 cursor-not-allowed",
                    "transition-all duration-200"
                  )}
                >
                  <div className="flex items-center justify-between w-full">
                    <span className="font-medium">{config.label}</span>
                    {model === modelKey && canUse && (
                      <div className="w-2 h-2 rounded-full bg-primary" />
                    )}
                  </div>
                  <span className="text-xs text-muted-foreground">
                    {config.modelName}
                  </span>
                  {!canUse && (
                    <Link
                      to="/dashboard/settings/billing"
                      onClick={(e) => e.stopPropagation()}
                      className="inline-flex items-center gap-1 mt-1 text-xs font-medium text-purple-600 dark:text-purple-400 hover:underline"
                    >
                      <Sparkles className="h-3 w-3" />
                      Upgrade
                    </Link>
                  )}
                </button>
              );
            }
          )}
        </div>
      </div>
    </div>
  );
}
